import { useLoading } from "context/LoadingContext";
import { ToastContext } from "context/ToastContext";
import {
  dateOnlyStringToCalendarDate,
  localCalendarDateToIsoDateString,
  normalizeCalendarDayToLocalNoon,
} from "helpers/date";
import useDocumentTitle from "hooks/useDocumentTitle";
import { BaseRateFileUploadDialog } from "pages/PayerRules/components/BaseRateFileUploadDialog";
import { useBaseRateFileKindConversion } from "pages/PayerRules/components/useBaseRateFileKindConversion";
import { Button } from "primereact/button";
import { Calendar } from "primereact/calendar";
import { Card } from "primereact/card";
import { Column } from "primereact/column";
import { DataTable } from "primereact/datatable";
import { Dropdown } from "primereact/dropdown";
import { FileUpload } from "primereact/fileupload";
import { InputNumber } from "primereact/inputnumber";
import { InputTextarea } from "primereact/inputtextarea";
import { Message } from "primereact/message";
import { MultiSelect } from "primereact/multiselect";
import { Tag } from "primereact/tag";
import { useContext, useState, useMemo, useEffect } from "react";
import { useNavigate, useSearchParams, useParams } from "react-router-dom";
import { useGetAllBaseRateFilesQuery } from "store/queries/baseRateFiles";
import {
  useCreateContractFeeScheduleMutation,
  useCreateBulkContractFeeScheduleMutation,
  useUploadAttachmentMutation,
  useGetContractFeeScheduleQuery,
  useGetContractFeeSchedulesQuery,
  useLazyGetContractFeeSchedulesQuery,
  RateCalculation,
  RateType,
  normalizeRateType,
  ICarveout,
  ICarveoutAttachment,
} from "store/queries/contractFeeSchedules";
import { useGetAllExplodingFeeSchedulesQuery } from "store/queries/explodingFeeSchedule";
import { useGetInpatientRulesetsQuery } from "store/queries/inpatientRulesets";
import { useGetAllPackageRulesetsQuery } from "store/queries/packageRuleset";
import { useGetAllPayersQuery } from "store/queries/payers";
import { useGetCaseRateRulesetsQuery } from "store/queries/pricing";
import { useGetAllDiscountRulesetsQuery } from "store/queries/procedureDiscountRuleset";
import { useGetAllProvidersQuery } from "store/queries/providers";

import RateCell from "./components/RateCell";
import UpdateTermFields from "./components/UpdateTermFields";
import { ContractUpdateTerm, normalizeUpdateTerms } from "./helpers";
import {
  contractCanLinkPayers,
  linkedPayerCopy,
  linkedPayerTypeForContractPayer,
} from "./linkedPayers";

const UPLOAD_NEW_SENTINEL = "__upload_new__";

interface IFormData {
  providerIds: string[];
  payerIds: number[];
  effectiveDate: Date | null;
  nextUpdateDate: Date | null;
  /** CLE-2781 — only meaningful with a nextUpdateDate; normalizeUpdateTerms keeps the pair legal. */
  updateTerm: ContractUpdateTerm | null;
  updatePercent: number | null;
  rateCalculations: RateCalculation[];
  attachmentFiles: File[];
  medicareAdvantagePayerIds: number[];
  notes?: string;
}

export function CreateContractFeeSchedulePage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { id: hospitalId } = useParams();
  const copyFromId = searchParams.get("copyFrom");
  const accountId = searchParams.get("accountId") || hospitalId;
  const hospitalOrAccountForContractLinks =
    hospitalId || accountId || undefined;

  useDocumentTitle(
    accountId
      ? "Create Contract Fee Schedule (Account Context)"
      : "Create Contract Fee Schedule"
  );
  const { setIsLoading } = useLoading();
  const { current: toastElement } = useContext(ToastContext);

  // Form state
  const [formData, setFormData] = useState<IFormData>({
    providerIds: [],
    payerIds: [],
    effectiveDate: null,
    nextUpdateDate: null,
    updateTerm: null,
    updatePercent: null,
    rateCalculations: [],
    attachmentFiles: [],
    medicareAdvantagePayerIds: [],
    notes: "",
  });

  // New rate calculation state
  const [newRateCalc, setNewRateCalc] = useState<{
    baseRateFileId: string;
    ratePercent: number;
    rateType: RateType;
  }>({
    baseRateFileId: "",
    ratePercent: 100,
    rateType: "percent",
  });

  /** Library carveout BaseRateFile rows (with %) to attach — same queue pattern as Edit Contract. */
  const [pendingCarveouts, setPendingCarveouts] = useState<
    ICarveoutAttachment[]
  >([]);

  const [showUploadDialog, setShowUploadDialog] = useState(false);

  /** CLE-2573 — key of the rate row whose rate is currently being edited in place. */
  const [editingRateKey, setEditingRateKey] = useState<string | null>(null);

  // Data fetching
  const { data: providers } = useGetAllProvidersQuery();
  const { data: payers } = useGetAllPayersQuery();
  const { data: baseRateFiles } = useGetAllBaseRateFilesQuery({
    accountId: accountId || undefined,
  });
  const { data: mpdrRulesets } = useGetAllDiscountRulesetsQuery();
  const { data: caseRateRulesets } = useGetCaseRateRulesetsQuery();
  const { data: packageRulesetsData } = useGetAllPackageRulesetsQuery();
  const { data: inpatientRulesetsData } = useGetInpatientRulesetsQuery();
  const { data: explodingFeeSchedulesData } =
    useGetAllExplodingFeeSchedulesQuery();
  const [createContract] = useCreateContractFeeScheduleMutation();
  const [createBulkContract] = useCreateBulkContractFeeScheduleMutation();
  const [uploadAttachment] = useUploadAttachmentMutation();

  // Fetch source contract if copying
  const { data: sourceContract } = useGetContractFeeScheduleQuery(
    copyFromId ?? "",
    {
      skip: !copyFromId,
    }
  );

  const singleProviderIdForDuplicateCheck =
    formData.providerIds.length === 1 ? formData.providerIds[0] : undefined;
  const singlePayerIdForDuplicateCheck =
    formData.payerIds.length === 1 ? formData.payerIds[0] : undefined;

  const {
    data: existingContractsForPair = [],
    refetch: refetchExistingContractPair,
  } = useGetContractFeeSchedulesQuery(
    {
      providerId: singleProviderIdForDuplicateCheck ?? "",
      payerId: singlePayerIdForDuplicateCheck ?? 0,
      ...(accountId ? { accountId } : {}),
    },
    {
      skip:
        !singleProviderIdForDuplicateCheck ||
        singlePayerIdForDuplicateCheck == null,
    }
  );

  const [lookupContractsForPair] = useLazyGetContractFeeSchedulesQuery();

  // Track selected providers (for backwards compat when rateType is missing)
  const selectedProviders =
    providers?.data?.filter((p: any) =>
      (formData.providerIds ?? []).includes(p.id)
    ) || [];

  // Track selected payers
  const selectedPayers =
    payers?.data?.filter((p: any) =>
      (formData.payerIds ?? []).includes(p.id)
    ) || [];
  // CLE-2751: the first selected payer that can lend its rates decides which payers may be
  // linked -- Original Medicare lends to MA payers, Medicaid lends to other Medicaid payers.
  const lendingPayer = selectedPayers.find((p: any) =>
    contractCanLinkPayers(p.type)
  );
  const linkablePayerType = linkedPayerTypeForContractPayer(lendingPayer?.type);
  const canLinkPayers = linkablePayerType !== null;
  const linkedCopy = linkedPayerCopy(lendingPayer?.type);

  // Clear linked payer IDs when no selected payer can lend its rates
  useEffect(() => {
    if (!canLinkPayers && formData.medicareAdvantagePayerIds.length > 0) {
      setFormData((prev) => ({
        ...prev,
        medicareAdvantagePayerIds: [],
      }));
    }
  }, [canLinkPayers]);

  // Loading state for submit button
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Track if we've already populated the form from the source contract
  const [hasPopulatedFromSource, setHasPopulatedFromSource] = useState(false);

  // Track whether to copy carveouts when copying a contract
  const [copyCarveouts, setCopyCarveouts] = useState(true);

  // Track existing rulesets for display purposes (to prevent duplicate creation)
  // These will be automatically linked when contract is created
  const [existingMpdrRulesets, setExistingMpdrRulesets] = useState<any[]>([]);
  const [existingCaseRateRulesets, setExistingCaseRateRulesets] = useState<
    any[]
  >([]);
  const [existingPackageRulesets, setExistingPackageRulesets] = useState<any[]>(
    []
  );
  const [existingInpatientRulesets, setExistingInpatientRulesets] = useState<
    any[]
  >([]);
  const [existingExplodingFeeSchedules, setExistingExplodingFeeSchedules] =
    useState<any[]>([]);

  // Populate form data when copying from another contract
  useEffect(() => {
    if (sourceContract?.data && copyFromId && !hasPopulatedFromSource) {
      const source = sourceContract.data;
      setFormData({
        providerIds: [source.providerId],
        payerIds: [source.payerId],
        effectiveDate: dateOnlyStringToCalendarDate(source.effectiveDate),
        nextUpdateDate: source.nextUpdateDate
          ? dateOnlyStringToCalendarDate(source.nextUpdateDate)
          : null,
        // CLE-2781 — a copied contract carries its update terms too; a Decimal arrives as a string.
        updateTerm: source.updateTerm ?? null,
        updatePercent:
          source.updatePercent === null || source.updatePercent === undefined
            ? null
            : Number(source.updatePercent),
        rateCalculations: (source.rateCalculations || []).map((calc) => ({
          ...calc,
          ratePercent: Number(calc.ratePercent),
          rateType: normalizeRateType(calc.rateType),
        })),
        attachmentFiles: [], // Don't copy files
        medicareAdvantagePayerIds:
          source.medicareAdvantagePlans?.map(
            (link: any) => link.medicareAdvantagePlanId
          ) || [],
      });
      setHasPopulatedFromSource(true);
    }
  }, [sourceContract, copyFromId]);

  // Fetch existing rulesets when provider/payer changes (for display only)
  useEffect(() => {
    // Only show existing rulesets when single provider and single payer are selected
    if (formData.providerIds.length === 1 && formData.payerIds.length === 1) {
      const providerId = formData.providerIds[0];
      const payerId = formData.payerIds[0];
      // Filter existing MPDR rulesets for this provider/payer combination
      const filteredMpdrRulesets =
        mpdrRulesets?.data?.filter(
          (ruleset: any) =>
            ruleset.provider?.id === providerId && ruleset.payer?.id === payerId
        ) || [];
      setExistingMpdrRulesets(filteredMpdrRulesets);

      // Filter existing case rate rulesets for this provider/payer combination
      const filteredCaseRateRulesets =
        caseRateRulesets?.data?.filter(
          (ruleset: any) =>
            ruleset.provider?.id === providerId && ruleset.payer?.id === payerId
        ) || [];
      setExistingCaseRateRulesets(filteredCaseRateRulesets);

      // Filter existing package rulesets for this provider/payer combination
      const filteredPackageRulesets =
        packageRulesetsData?.data?.filter(
          (ruleset: any) =>
            ruleset.provider?.id === providerId && ruleset.payer?.id === payerId
        ) || [];
      setExistingPackageRulesets(filteredPackageRulesets);

      // Filter existing inpatient rulesets for this provider/payer combination
      const filteredInpatientRulesets =
        inpatientRulesetsData?.data?.filter(
          (ruleset: any) =>
            ruleset.provider?.id === providerId && ruleset.payer?.id === payerId
        ) || [];
      setExistingInpatientRulesets(filteredInpatientRulesets);

      // Filter existing exploding fee schedule rulesets for this provider/payer combination
      const filteredExplodingFeeSchedules =
        explodingFeeSchedulesData?.data?.filter(
          (ruleset: any) =>
            ruleset.provider?.id === providerId && ruleset.payer?.id === payerId
        ) || [];
      setExistingExplodingFeeSchedules(filteredExplodingFeeSchedules);
    } else {
      setExistingMpdrRulesets([]);
      setExistingCaseRateRulesets([]);
      setExistingPackageRulesets([]);
      setExistingInpatientRulesets([]);
      setExistingExplodingFeeSchedules([]);
    }
  }, [
    formData.providerIds,
    formData.payerIds,
    mpdrRulesets?.data,
    caseRateRulesets?.data,
    packageRulesetsData?.data,
    inpatientRulesetsData?.data,
    explodingFeeSchedulesData?.data,
  ]);

  // Options for dropdowns
  const providerOptions = useMemo(() => {
    if (!providers?.data) return [];

    // If we're in account context, filter providers to only show those from this account
    let filteredProviders = providers.data;
    if (accountId) {
      filteredProviders = providers.data.filter(
        (p: any) => p.accountId === accountId
      );
    }

    return filteredProviders.map((p: any) => ({ label: p.name, value: p.id }));
  }, [providers, accountId]);

  const payerOptions = useMemo(() => {
    return (
      payers?.data?.map((p: any) => ({ label: p.name, value: p.id })) || []
    );
  }, [payers]);

  // Single dropdown of both base rate files and carveouts (carveout options
  // tagged and hidden once queued). Both are scoped to the account.
  const accountScopedFiles = useMemo(() => {
    let files = baseRateFiles?.data ?? [];
    if (accountId) {
      files = files.filter((f) => !f.accountId || f.accountId === accountId);
    }
    return files;
  }, [baseRateFiles, accountId]);

  const baseRateFileOptions = useMemo(() => {
    const pendingCarveoutIds = new Set(
      pendingCarveouts.map((c) => c.baseRateFileId)
    );
    return [
      { label: "+ Upload new file…", value: UPLOAD_NEW_SENTINEL },
      ...accountScopedFiles
        .filter((f) => !(f.isCarveout && pendingCarveoutIds.has(f.id)))
        .map((f) => ({
          label: f.isCarveout
            ? `${f.displayName} (Carveout, v${f.version})`
            : f.displayName,
          value: f.id,
        })),
    ];
  }, [accountScopedFiles, pendingCarveouts]);

  /** Look up a base rate file by id (to decide carveout vs base and resolve display names). */
  const baseRateFileById = useMemo(() => {
    const map = new Map<
      string,
      {
        id: string;
        displayName?: string;
        isCarveout?: boolean;
        version?: number;
      }
    >();
    (baseRateFiles?.data ?? []).forEach((f) => map.set(f.id, f));
    return map;
  }, [baseRateFiles?.data]);

  /**
   * CLE-2567 — nothing here is saved yet, so a file picked under the wrong kind can
   * still be converted in place. The lookup keeps the check honest: a file already
   * attached to some other contract is blocked with links to it.
   */
  const rateRowFileIds = useMemo(() => {
    const ids = [
      ...formData.rateCalculations.map((c) => c.baseRateFileId),
      ...pendingCarveouts.map((c) => c.baseRateFileId),
    ].filter((id): id is string => typeof id === "string" && id.length > 0);
    return [...new Set(ids)];
  }, [formData.rateCalculations, pendingCarveouts]);

  const { openConvertDialog } = useBaseRateFileKindConversion({
    fileIds: rateRowFileIds,
    accountId: hospitalId || undefined,
    showToast: (message) => toastElement?.show(message),
  });

  const medicareAdvantagePayerOptions = useMemo(() => {
    return (
      payers?.data
        ?.filter(
          (p: any) =>
            p.type === linkablePayerType &&
            !(formData.payerIds ?? []).includes(p.id)
        )
        ?.map((p: any) => ({ label: p.name, value: p.id })) || []
    );
  }, [payers, linkablePayerType, formData.payerIds]);

  const handleInputChange = (field: keyof IFormData, value: any) => {
    // MultiSelect returns null when cleared; normalize to [] for array fields
    let normalizedValue =
      (field === "providerIds" || field === "payerIds") && value == null
        ? []
        : value;
    if (
      (field === "effectiveDate" || field === "nextUpdateDate") &&
      normalizedValue instanceof Date
    ) {
      normalizedValue = normalizeCalendarDayToLocalNoon(normalizedValue);
    }
    setFormData((prev) => {
      const next = { ...prev, [field]: normalizedValue };
      // CLE-2781 — clearing the next update date, or switching off Percent Increase, must not
      // leave a term or a percent stranded. Re-derived on every change so the form state is
      // always a body the API will accept.
      return { ...next, ...normalizeUpdateTerms(next.nextUpdateDate, next) };
    });
  };

  const resetNewRateCalc = () =>
    setNewRateCalc({
      baseRateFileId: "",
      ratePercent: 100,
      rateType: "percent",
    });

  const addRateCalculation = () => {
    if (newRateCalc.ratePercent <= 0) {
      toastElement?.show({
        severity: "warn",
        summary: "Invalid Rate Calculation",
        detail:
          newRateCalc.rateType === "unit"
            ? "Please enter a valid unit price."
            : "Please enter a valid rate percentage.",
      });
      return;
    }

    // A carveout base rate file attaches as a ContractCarveout (with its %); a
    // normal base rate file becomes a ContractRateCalculation.
    const selectedFile = newRateCalc.baseRateFileId
      ? baseRateFileById.get(newRateCalc.baseRateFileId)
      : undefined;

    if (selectedFile?.isCarveout) {
      if (pendingCarveouts.some((c) => c.baseRateFileId === selectedFile.id)) {
        resetNewRateCalc();
        return;
      }
      setPendingCarveouts((prev) => [
        ...prev,
        {
          baseRateFileId: selectedFile.id,
          ratePercent: Number(newRateCalc.ratePercent),
          rateType: newRateCalc.rateType,
        },
      ]);
      resetNewRateCalc();
      return;
    }

    const rateCalculation: RateCalculation = {
      id: "", // Will be set by backend
      baseRateFileId: newRateCalc.baseRateFileId || undefined,
      ratePercent: Number(newRateCalc.ratePercent),
      rateType: newRateCalc.rateType,
      sortOrder: formData.rateCalculations.length,
    };

    setFormData((prev) => ({
      ...prev,
      rateCalculations: [...prev.rateCalculations, rateCalculation],
    }));

    resetNewRateCalc();
  };

  const removeRateCalculation = (index: number) => {
    setFormData((prev) => ({
      ...prev,
      rateCalculations: prev.rateCalculations.filter((_, i) => i !== index),
    }));
  };

  /**
   * CLE-2264 — after a clone create, the API reports how many adjustment rules
   * were copied from the source pair. Silent when the pair is the same (the
   * rules already apply) or when no summary came back.
   */
  const showRulesCopySummaryToast = (result: unknown) => {
    // The summary rides on each created contract: single create → result.data,
    // bulk create → each element of result.data[].
    const r = result as any;
    const summaries = [
      r?.data?.rulesCopySummary,
      ...(Array.isArray(r?.data)
        ? r.data.map((c: any) => c?.rulesCopySummary)
        : []),
    ].filter((s: any) => s && !s.samePair);
    if (summaries.length === 0) return;
    const copied = summaries.reduce(
      (sum: number, s: any) => sum + (s.copied ?? 0),
      0
    );
    const skipped = summaries.reduce(
      (sum: number, s: any) => sum + (s.skipped ?? 0),
      0
    );
    if (copied === 0 && skipped === 0) return;
    toastElement?.show({
      severity: "success",
      summary: `Copied ${copied} adjustment rules${
        skipped ? `, ${skipped} skipped` : ""
      }`,
    });
  };

  const handleSubmit = async () => {
    // Validation
    if (formData.providerIds.length === 0) {
      toastElement?.show({
        severity: "warn",
        summary: "Validation Error",
        detail: "Please select at least one provider.",
      });
      return;
    }

    if (formData.payerIds.length === 0) {
      toastElement?.show({
        severity: "warn",
        summary: "Validation Error",
        detail: "Please select at least one payer.",
      });
      return;
    }

    if (!formData.effectiveDate) {
      toastElement?.show({
        severity: "warn",
        summary: "Validation Error",
        detail: "Effective date is required.",
      });
      return;
    }

    const effectiveDateIso = localCalendarDateToIsoDateString(
      formData.effectiveDate
    );
    if (!effectiveDateIso) {
      toastElement?.show({
        severity: "warn",
        summary: "Validation Error",
        detail: "Effective date is invalid.",
      });
      return;
    }

    if (formData.rateCalculations.length === 0) {
      toastElement?.show({
        severity: "warn",
        summary: "Validation Error",
        detail:
          "At least one rate calculation is required to create a contract fee schedule.",
      });
      return;
    }

    // CLE-2781 — a percent increase with no percent is the one update-term state the form can
    // still reach (the field is required but nothing blocks an empty submit).
    if (
      formData.nextUpdateDate &&
      formData.updateTerm === "PercentIncrease" &&
      formData.updatePercent === null
    ) {
      toastElement?.show({
        severity: "warn",
        summary: "Validation Error",
        detail:
          "Enter the percent increase, or choose a different update term.",
      });
      return;
    }

    const isMultipleCreation =
      formData.providerIds.length > 1 || formData.payerIds.length > 1;

    if (!isMultipleCreation) {
      const refreshed = await refetchExistingContractPair();
      if (refreshed.error) {
        toastElement?.show({
          severity: "error",
          summary: "Unable to verify",
          detail:
            "We could not check whether a contract already exists for this provider and payer. Try again.",
        });
        return;
      }
      const pairRows = refreshed.data;
      if (Array.isArray(pairRows) && pairRows.length > 0) {
        toastElement?.show({
          severity: "warn",
          summary: "Contract already exists",
          detail:
            "A contract already exists for this provider and payer. Open that contract and add a new term instead of creating another contract.",
        });
        return;
      }
    } else {
      const pairs = formData.providerIds.flatMap((pid) =>
        formData.payerIds.map((pyid) => ({ pid, pyid }))
      );
      let conflicts: string[] = [];
      try {
        const lookupResults = await Promise.all(
          pairs.map(({ pid, pyid }) =>
            lookupContractsForPair({
              providerId: pid,
              payerId: pyid,
              ...(accountId ? { accountId } : {}),
            })
              .unwrap()
              .then((rows) => ({ pid, pyid, rows }))
          )
        );
        conflicts = lookupResults
          .filter(({ rows }) => rows.length > 0)
          .map(({ pid, pyid }) => {
            const pn =
              providers?.data?.find((p: any) => p.id === pid)?.name ?? pid;
            const payn =
              payers?.data?.find((p: any) => p.id === pyid)?.name ??
              String(pyid);
            return `${pn} – ${payn}`;
          });
      } catch {
        toastElement?.show({
          severity: "error",
          summary: "Unable to verify",
          detail: "We could not check for existing contracts. Try again.",
        });
        return;
      }
      if (conflicts.length > 0) {
        toastElement?.show({
          severity: "warn",
          summary: "Contract already exists",
          detail: `A contract already exists for: ${conflicts.join(
            "; "
          )}. Add a new term on each existing contract instead of creating another.`,
        });
        return;
      }
    }

    try {
      setIsLoading(true);
      setIsSubmitting(true);

      // Check if we need to use bulk creation (same as isMultipleCreation above)
      const totalContracts =
        formData.providerIds.length * formData.payerIds.length;

      if (isMultipleCreation) {
        // Use bulk creation
        const bulkData = {
          providerIds: formData.providerIds,
          payerIds: formData.payerIds,
          effectiveDate: effectiveDateIso,
          nextUpdateDate: localCalendarDateToIsoDateString(
            formData.nextUpdateDate
          ),
          // CLE-2781 — always sent, null included, so the API stores exactly what the form shows.
          ...normalizeUpdateTerms(formData.nextUpdateDate, formData),
          rateCalculations: formData.rateCalculations.map((calc, index) => ({
            baseRateFileId: calc.baseRateFileId,
            ratePercent: calc.ratePercent,
            rateType: calc.rateType ?? "percent",
            sortOrder: index,
          })),
          medicareAdvantagePayerIds:
            formData.medicareAdvantagePayerIds.length > 0
              ? formData.medicareAdvantagePayerIds
              : undefined,
          copyFromContractId:
            copyFromId && copyCarveouts ? copyFromId : undefined,
          // CLE-2264 — cloning also copies the source pair's adjustment rules.
          copyRulesFromContractId: copyFromId || undefined,
          carveouts: pendingCarveouts.length > 0 ? pendingCarveouts : undefined,
          notes: formData.notes || undefined,
        };

        toastElement?.show({
          severity: "info",
          summary: "Creating Contracts",
          detail: `Creating ${totalContracts} contract(s)...`,
        });

        const result = await createBulkContract(bulkData).unwrap();

        toastElement?.show({
          severity: "success",
          summary: "Contracts Created",
          detail:
            pendingCarveouts.length > 0
              ? `${result.message} with ${pendingCarveouts.length} carveout file(s) attached.`
              : result.message,
        });

        showRulesCopySummaryToast(result);

        // Navigate to contracts list - handle both hospital and admin contexts
        if (hospitalId) {
          navigate(`/hospitals/view/${hospitalId}/contracts/library`);
        } else {
          navigate(`/payer-rules?tab=2&baseSubtab=files`);
        }
      } else {
        // Single contract creation
        const contractData = {
          providerId: formData.providerIds[0],
          payerId: formData.payerIds[0],
          effectiveDate: effectiveDateIso,
          nextUpdateDate: localCalendarDateToIsoDateString(
            formData.nextUpdateDate
          ),
          // CLE-2781 — always sent, null included, so the API stores exactly what the form shows.
          ...normalizeUpdateTerms(formData.nextUpdateDate, formData),
          rateCalculations: formData.rateCalculations.map((calc, index) => ({
            baseRateFileId: calc.baseRateFileId,
            ratePercent: calc.ratePercent,
            rateType: calc.rateType ?? "percent",
            sortOrder: index,
          })),
          medicareAdvantagePayerIds:
            formData.medicareAdvantagePayerIds.length > 0
              ? formData.medicareAdvantagePayerIds
              : undefined,
          copyFromContractId:
            copyFromId && copyCarveouts ? copyFromId : undefined,
          // CLE-2264 — cloning also copies the source pair's adjustment rules.
          copyRulesFromContractId: copyFromId || undefined,
          carveouts: pendingCarveouts.length > 0 ? pendingCarveouts : undefined,
          notes: formData.notes || undefined,
        };

        const result = await createContract(contractData as any).unwrap();

        // Upload attachment files if any were selected
        if (formData.attachmentFiles.length > 0) {
          try {
            toastElement?.show({
              severity: "info",
              summary: "Uploading Attachments",
              detail: `Uploading ${formData.attachmentFiles.length} attachment file(s)...`,
            });

            const attachmentPromises = formData.attachmentFiles.map((file) =>
              uploadAttachment({
                contractId: result.id,
                file,
              }).unwrap()
            );

            await Promise.all(attachmentPromises);
          } catch (attachmentError) {
            toastElement?.show({
              severity: "warn",
              summary: "Contract Created",
              detail:
                "Contract was created but some attachment files failed to upload. You can upload them from the edit page.",
            });
          }
        }

        if (formData.attachmentFiles.length === 0) {
          toastElement?.show({
            severity: "success",
            summary: "Contract Created",
            detail: "Contract fee schedule has been created successfully.",
          });
        } else {
          toastElement?.show({
            severity: "success",
            summary: "Contract Created",
            detail: "Contract fee schedule and files uploaded successfully.",
          });
        }

        showRulesCopySummaryToast(result);

        // Navigate to contract view - handle both hospital and admin contexts
        if (hospitalId) {
          navigate(`/hospitals/view/${hospitalId}/contracts/library`);
        } else {
          navigate(`/payer-rules/contracts/${result.id}`);
        }
      }
    } catch (error: any) {
      const errorMessage =
        error?.data?.messages?.[0] ||
        error?.data?.message ||
        "Failed to create contract fee schedule.";
      toastElement?.show({
        severity: "error",
        summary: "Error",
        detail: errorMessage,
      });
    } finally {
      setIsLoading(false);
      setIsSubmitting(false);
    }
  };

  const getRateTypeForDisplay = (calc: RateCalculation): RateType =>
    normalizeRateType(calc.rateType);

  /**
   * One row per attached rate source — base rate calculations and queued
   * carveouts merged into a single list, each with a tag and its own percentage.
   */
  interface IUnifiedRateRow {
    key: string;
    name: string;
    isCarveout: boolean;
    ratePercent: number;
    rateType: RateType;
    /** CLE-2573 — correct a mistyped rate in place instead of re-attaching. */
    onSaveRate: (ratePercent: number, rateType: RateType) => void;
    onRemove: () => void;
    /** CLE-2567 — convert this row's file between base rate and carve-out. */
    onConvert?: () => void;
  }

  /**
   * CLE-2567 — a converted file has to move to the other list, or the create call
   * would send it under the wrong kind and be rejected by the API's check.
   */
  const convertRateCalculationRow = (index: number) => {
    const calc = formData.rateCalculations[index];
    if (!calc?.baseRateFileId) return;
    setFormData((prev) => ({
      ...prev,
      rateCalculations: prev.rateCalculations.filter((_, i) => i !== index),
    }));
    setPendingCarveouts((prev) => [
      ...prev,
      {
        baseRateFileId: calc.baseRateFileId as string,
        ratePercent: Number(calc.ratePercent),
        rateType: normalizeRateType(calc.rateType),
      },
    ]);
  };

  const convertPendingCarveoutRow = (index: number) => {
    const carveout = pendingCarveouts[index];
    if (!carveout) return;
    setPendingCarveouts((prev) => prev.filter((_, i) => i !== index));
    setFormData((prev) => ({
      ...prev,
      rateCalculations: [
        ...prev.rateCalculations,
        {
          baseRateFileId: carveout.baseRateFileId,
          ratePercent: carveout.ratePercent ?? 100,
          rateType: carveout.rateType ?? "percent",
          sortOrder: prev.rateCalculations.length,
        },
      ],
    }));
  };

  const buildUnifiedRateRows = (): IUnifiedRateRow[] => {
    const rows: IUnifiedRateRow[] = [];

    formData.rateCalculations.forEach((calc, index) => {
      rows.push({
        key: `rc-${index}`,
        name: calc.baseRateFileId
          ? baseRateFileById.get(calc.baseRateFileId)?.displayName ??
            "Base Rate File"
          : "No base rate file",
        isCarveout: false,
        ratePercent: Number(calc.ratePercent),
        rateType: getRateTypeForDisplay(calc),
        onSaveRate: (ratePercent, rateType) =>
          setFormData((prev) => ({
            ...prev,
            rateCalculations: prev.rateCalculations.map((rc, i) =>
              i === index ? { ...rc, ratePercent, rateType } : rc
            ),
          })),
        onRemove: () => removeRateCalculation(index),
        onConvert: calc.baseRateFileId
          ? () =>
              openConvertDialog(
                {
                  id: calc.baseRateFileId as string,
                  displayName: baseRateFileById.get(calc.baseRateFileId ?? "")
                    ?.displayName,
                  isCarveout: false,
                },
                {
                  warning:
                    formData.rateCalculations.length === 1
                      ? "This is the only base rate file on this contract. A carve-out only overrides the CPT codes it lists, so the contract would be left with no base pricing."
                      : undefined,
                  onConverted: () => convertRateCalculationRow(index),
                }
              )
          : undefined,
      });
    });

    pendingCarveouts.forEach((c, index) => {
      rows.push({
        key: `pco-${c.baseRateFileId}`,
        name:
          baseRateFileById.get(c.baseRateFileId)?.displayName ??
          c.baseRateFileId,
        isCarveout: true,
        ratePercent: c.ratePercent != null ? Number(c.ratePercent) : 100,
        rateType: c.rateType ?? "percent",
        onSaveRate: (ratePercent, rateType) =>
          setPendingCarveouts((prev) =>
            prev.map((pc, i) =>
              i === index ? { ...pc, ratePercent, rateType } : pc
            )
          ),
        onRemove: () =>
          setPendingCarveouts((prev) => prev.filter((_, i) => i !== index)),
        onConvert: () =>
          openConvertDialog(
            {
              id: c.baseRateFileId,
              displayName: baseRateFileById.get(c.baseRateFileId)?.displayName,
              isCarveout: true,
            },
            { onConverted: () => convertPendingCarveoutRow(index) }
          ),
      });
    });

    return rows;
  };

  const unifiedFileBody = (row: IUnifiedRateRow) => (
    <span className="font-medium">{row.name}</span>
  );

  const unifiedTypeBody = (row: IUnifiedRateRow) =>
    row.isCarveout ? (
      <Tag value="Carve-out" severity="warning" />
    ) : (
      <Tag value="Base rate" severity="info" />
    );

  const handleSaveRate = (
    row: IUnifiedRateRow,
    ratePercent: number,
    rateType: RateType
  ) => {
    if (ratePercent <= 0) {
      toastElement?.show({
        severity: "warn",
        summary: "Invalid Rate",
        detail:
          rateType === "unit"
            ? "Please enter a valid unit price."
            : "Please enter a valid rate percentage.",
      });
      return;
    }
    row.onSaveRate(ratePercent, rateType);
    setEditingRateKey(null);
  };

  const unifiedRateBody = (row: IUnifiedRateRow) => (
    <RateCell
      ratePercent={row.ratePercent}
      rateType={row.rateType}
      editing={editingRateKey === row.key}
      onBeginEdit={() => setEditingRateKey(row.key)}
      onCancel={() => setEditingRateKey(null)}
      onSave={(ratePercent, rateType) =>
        handleSaveRate(row, ratePercent, rateType)
      }
    />
  );

  const unifiedActionsBody = (row: IUnifiedRateRow) => (
    <div className="flex align-items-center">
      {row.onConvert && (
        <Button
          icon={row.isCarveout ? "pi pi-file" : "pi pi-file-edit"}
          className="p-button-rounded p-button-text"
          tooltip={
            row.isCarveout
              ? "Convert to base rate file"
              : "Convert to carve-out file"
          }
          tooltipOptions={{ position: "top" }}
          onClick={row.onConvert}
        />
      )}
      <Button
        icon="pi pi-trash"
        className="p-button-rounded p-button-danger p-button-text"
        onClick={row.onRemove}
      />
    </div>
  );

  const attachmentFileActionsTemplate = (rowData: File, options: any) => (
    <Button
      icon="pi pi-times"
      className="p-button-rounded p-button-text p-button-danger p-button-sm"
      onClick={() => {
        const newFiles = formData.attachmentFiles.filter(
          (_, index) => index !== options.rowIndex
        );
        handleInputChange("attachmentFiles", newFiles);
      }}
      tooltip="Remove file"
    />
  );

  const firstExistingContractId = existingContractsForPair[0]?.id;
  let openExistingContractPath: string | undefined;
  if (firstExistingContractId) {
    if (hospitalOrAccountForContractLinks) {
      openExistingContractPath = `/hospitals/view/${hospitalOrAccountForContractLinks}/contracts/${firstExistingContractId}`;
    } else {
      openExistingContractPath = `/payer-rules/contracts/${firstExistingContractId}`;
    }
  }

  const hasBlockingDuplicateForSinglePair =
    formData.providerIds.length === 1 &&
    formData.payerIds.length === 1 &&
    existingContractsForPair.length > 0;

  return (
    <div className="card">
      {!hospitalId && (
        <div className="mb-3">
          <Button
            label="Return to Contract Fee Schedules"
            icon="pi pi-arrow-left"
            className="p-button-text p-button-plain text-600"
            onClick={() => navigate(`/payer-rules?tab=2&baseSubtab=files`)}
          />
        </div>
      )}
      <div className="mb-4">
        <h2>Create Contract Fee Schedule</h2>
      </div>

      {/* Copy notification banner */}
      {copyFromId && sourceContract?.data && (
        <div className="mb-4 p-3 border-1 border-300 border-round bg-blue-50">
          <div className="flex align-items-center">
            <i className="pi pi-copy text-blue-600 mr-2 text-2xl" />
            <div>
              <div className="text-blue-800 font-bold">
                Copying from existing contract
              </div>
              <div className="text-blue-700 text-sm mt-1">
                This new contract will be created with settings copied from{" "}
                {sourceContract.data.provider?.name} -{" "}
                {sourceContract.data.payer?.name}
                {sourceContract.data.carveOutFiles &&
                  sourceContract.data.carveOutFiles.length > 0 &&
                  copyCarveouts &&
                  ` including ${sourceContract.data.carveOutFiles.length} carveout file(s)`}
              </div>
              <div className="text-blue-700 text-sm mt-1">
                Adjustment rules from the source contract&apos;s provider +
                payer will be copied to the new provider + payer automatically.
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="grid">
        {/* Basic Information */}
        <div className="col-12">
          <Card title="Basic Information" className="mb-4">
            <div className="grid">
              <div className="col-12 md:col-6">
                <label className="block text-900 font-medium mb-2">
                  Provider(s) *
                </label>
                <MultiSelect
                  value={formData.providerIds}
                  options={providerOptions}
                  onChange={(e) => handleInputChange("providerIds", e.value)}
                  placeholder="Select Provider(s)"
                  className="w-full"
                  filter
                  display="chip"
                  showClear
                />
                {formData.providerIds.length > 0 && (
                  <small className="text-600 mt-1 block">
                    {formData.providerIds.length} provider(s) selected
                  </small>
                )}
              </div>
              <div className="col-12 md:col-6">
                <label className="block text-900 font-medium mb-2">
                  Payer(s) *
                </label>
                <MultiSelect
                  value={formData.payerIds}
                  options={payerOptions}
                  onChange={(e) => handleInputChange("payerIds", e.value)}
                  placeholder="Select Payer(s)"
                  className="w-full"
                  filter
                  display="chip"
                  showClear
                />
                {formData.payerIds.length > 0 && (
                  <small className="text-600 mt-1 block">
                    {formData.payerIds.length} payer(s) selected
                  </small>
                )}
              </div>
              {hasBlockingDuplicateForSinglePair && (
                <div className="col-12">
                  <Message
                    severity="warn"
                    className="w-full"
                    text="A contract already exists for this provider and payer. Add a new term on that contract instead of creating another one."
                  />
                  {openExistingContractPath && (
                    <Button
                      type="button"
                      label="Open existing contract"
                      icon="pi pi-external-link"
                      className="p-button-text mt-2"
                      onClick={() => {
                        if (openExistingContractPath) {
                          navigate(openExistingContractPath);
                        }
                      }}
                    />
                  )}
                </div>
              )}
              <div className="col-12 md:col-6">
                <label className="block text-900 font-medium mb-2">
                  Effective Date *
                </label>
                <Calendar
                  value={formData.effectiveDate}
                  onChange={(e) => handleInputChange("effectiveDate", e.value)}
                  placeholder="Select Effective Date"
                  className="w-full"
                  showIcon
                />
              </div>
              <div className="col-12 md:col-6">
                <label className="block text-900 font-medium mb-2">
                  Next Update Date
                </label>
                <Calendar
                  value={formData.nextUpdateDate}
                  onChange={(e) => handleInputChange("nextUpdateDate", e.value)}
                  placeholder="Select Next Update Date"
                  className="w-full"
                  showIcon
                />
              </div>
              <UpdateTermFields
                nextUpdateDate={formData.nextUpdateDate}
                updateTerm={formData.updateTerm}
                updatePercent={formData.updatePercent}
                onUpdateTermChange={(v) => handleInputChange("updateTerm", v)}
                onUpdatePercentChange={(v) =>
                  handleInputChange("updatePercent", v)
                }
              />
              <div className="col-12">
                <label className="block text-900 font-medium mb-2">Notes</label>
                <InputTextarea
                  value={formData.notes || ""}
                  onChange={(e) => handleInputChange("notes", e.target.value)}
                  placeholder="Enter any notes or special instructions for this contract..."
                  rows={4}
                  className="w-full"
                  autoResize
                />
              </div>
            </div>
          </Card>
        </div>

        {/* Linked payers - show when a selected payer can lend its rates (CLE-2751) */}
        {canLinkPayers && (
          <div className="col-12">
            <Card title={linkedCopy.title} className="mb-4">
              <p className="mb-3 text-600">{linkedCopy.help}</p>
              <MultiSelect
                value={formData.medicareAdvantagePayerIds}
                options={medicareAdvantagePayerOptions}
                onChange={(e) =>
                  handleInputChange("medicareAdvantagePayerIds", e.value)
                }
                placeholder={linkedCopy.placeholder}
                className="w-full"
                display="chip"
                showClear
                filter
              />
              <small className="text-600 mt-2 block">
                These payers will be linked only to the {lendingPayer?.name}{" "}
                contract.
              </small>
            </Card>
          </div>
        )}

        {/* Rate Calculations (base rate files + carve-outs) */}
        <div className="col-12">
          <Card title="Rate Calculations" className="mb-4">
            <p className="mb-3 text-600">
              Attach base rate files and carve-outs. Carve-out files (tagged
              below) override specific CPT rates; both take a percentage.
            </p>

            {/* Show source contract carveouts when copying */}
            {copyFromId &&
              sourceContract?.data?.carveOutFiles &&
              sourceContract.data.carveOutFiles.length > 0 && (
                <div className="mb-3 p-3 border-1 border-300 border-round bg-blue-50">
                  <div className="flex align-items-start mb-2">
                    <input
                      type="checkbox"
                      id="copyCarveouts"
                      checked={copyCarveouts}
                      onChange={(e) => setCopyCarveouts(e.target.checked)}
                      className="mr-2 mt-1"
                    />
                    <label
                      htmlFor="copyCarveouts"
                      className="flex-grow-1 cursor-pointer"
                    >
                      <span className="text-blue-800 font-medium">
                        Copy carveout attachment(s) from the source contract
                      </span>
                      {copyCarveouts && (
                        <ul className="m-0 pl-3 mt-2">
                          {sourceContract.data.carveOutFiles.map(
                            (file: ICarveout) => (
                              <li key={file.id} className="text-blue-700">
                                {file.fileName}
                                {file.baseRateFileId
                                  ? " (library link)"
                                  : " (legacy upload)"}
                              </li>
                            )
                          )}
                        </ul>
                      )}
                    </label>
                  </div>
                  {!copyCarveouts && (
                    <div className="text-blue-700 text-sm mt-2 ml-4">
                      <i className="pi pi-exclamation-triangle mr-1" />
                      Carveouts will not be copied from the source contract.
                    </div>
                  )}
                </div>
              )}

            <div className="mb-4">
              <div className="grid">
                <div className="col-12 md:col-5">
                  <label className="block text-900 font-medium mb-2">
                    Base Rate / Carve-Out File
                  </label>
                  <Dropdown
                    value={newRateCalc.baseRateFileId}
                    options={baseRateFileOptions}
                    onChange={(e) => {
                      if (e.value === UPLOAD_NEW_SENTINEL) {
                        setShowUploadDialog(true);
                        return;
                      }
                      setNewRateCalc((prev) => ({
                        ...prev,
                        baseRateFileId: e.value,
                      }));
                    }}
                    placeholder="Select file"
                    className="w-full"
                    filter
                  />
                </div>
                <div className="col-12 md:col-2">
                  <label className="block text-900 font-medium mb-2">
                    Type
                  </label>
                  <Dropdown
                    value={newRateCalc.rateType}
                    options={[
                      { label: "$", value: "unit" },
                      { label: "%", value: "percent" },
                    ]}
                    onChange={(e) => {
                      const newType = (e.value ?? "percent") as RateType;
                      setNewRateCalc((prev) => ({
                        ...prev,
                        rateType: newType,
                        ratePercent: 100,
                      }));
                    }}
                    placeholder="Select type"
                    className="w-full"
                  />
                </div>
                <div className="col-12 md:col-3">
                  <label className="block text-900 font-medium mb-2">
                    {newRateCalc.rateType === "unit"
                      ? "Price per Unit"
                      : "Rate Percentage"}
                  </label>
                  <div className="p-inputgroup">
                    <InputNumber
                      value={
                        newRateCalc.rateType === "unit"
                          ? newRateCalc.ratePercent / 100
                          : newRateCalc.ratePercent
                      }
                      onValueChange={(e) =>
                        setNewRateCalc((prev) => ({
                          ...prev,
                          ratePercent:
                            prev.rateType === "unit"
                              ? (e.value || 0) * 100
                              : e.value || 0,
                        }))
                      }
                      placeholder={
                        newRateCalc.rateType === "unit"
                          ? "Price per Unit"
                          : "Rate %"
                      }
                      className="w-full"
                      mode="decimal"
                      min={0}
                      max={100000}
                      minFractionDigits={0}
                      maxFractionDigits={
                        newRateCalc.rateType === "unit" ? 4 : 2
                      }
                    />
                    {newRateCalc.rateType !== "unit" && (
                      <span className="p-inputgroup-addon">%</span>
                    )}
                  </div>
                </div>
                <div className="col-12 md:col-2 flex align-items-end">
                  <Button
                    label="Add"
                    icon="pi pi-plus"
                    onClick={addRateCalculation}
                    className="w-full"
                  />
                </div>
              </div>
            </div>

            <DataTable
              value={buildUnifiedRateRows()}
              dataKey="key"
              emptyMessage="No rate calculations added yet."
              className="mt-3"
            >
              <Column header="File" body={unifiedFileBody} />
              <Column
                header="Type"
                body={unifiedTypeBody}
                style={{ width: "130px" }}
              />
              <Column
                header="Rate"
                body={unifiedRateBody}
                style={{ width: "280px" }}
              />
              <Column
                header="Actions"
                body={unifiedActionsBody}
                style={{ width: "100px" }}
              />
            </DataTable>
          </Card>
        </div>
        {/* MPDR & Case Rate Rulesets (applied automatically by provider + payer) */}
        <div className="col-12">
          <Card title="MPDR & Case Rate Rulesets" className="mb-4">
            <p className="mb-3 text-600">
              Adjustment rules are not selected per contract — they apply
              automatically based on the provider and payer.
            </p>
            {/* MPDR Rule Sets */}
            <div className="mb-4">
              {/* Show existing MPDR rulesets if single provider/payer selected */}
              {existingMpdrRulesets.length > 0 && (
                <div className="mt-3 p-3 border-1 border-300 border-round bg-blue-50">
                  <h6 className="mt-0 mb-2 text-blue-800">
                    <i className="pi pi-info-circle mr-2" />
                    Existing MPDR Rule Sets for {
                      selectedProviders[0]?.name
                    } - {selectedPayers[0]?.name}
                  </h6>
                  <p className="text-sm text-blue-700 mb-3">
                    These rulesets apply to this provider + payer combination
                    automatically:
                  </p>
                  {existingMpdrRulesets.map((ruleset, index) => (
                    <div
                      key={ruleset.id}
                      className={
                        index < existingMpdrRulesets.length - 1
                          ? "pb-2 mb-2 border-bottom"
                          : ""
                      }
                    >
                      <div className="font-medium">
                        <a
                          href={`/payer-rules/mppr/edit/${ruleset.id}`}
                          onClick={(e) => {
                            e.preventDefault();
                            navigate(`/payer-rules/mppr/edit/${ruleset.id}`);
                          }}
                          className="text-primary no-underline hover:underline cursor-pointer"
                        >
                          {ruleset.groupName || "Unnamed MPDR Rule Set"}
                        </a>
                      </div>
                      <div className="text-sm text-600 mt-1">
                        Rank 2: {ruleset.discountPercentForRank2}%, Rank 3:{" "}
                        {ruleset.discountPercentForRank3}%, Rank 4:{" "}
                        {ruleset.discountPercentForRank4}%, Rank 5+:{" "}
                        {ruleset.discountPercentForRank5Plus}%
                      </div>
                      <div className="text-sm text-600">
                        {ruleset.cptCodes && ruleset.cptCodes.length > 0 ? (
                          <span>
                            CPT Codes:{" "}
                            {ruleset.cptCodes.slice(0, 10).join(", ")}
                            {ruleset.cptCodes.length > 10 && (
                              <span>
                                , and {ruleset.cptCodes.length - 10} other CPT
                                codes
                              </span>
                            )}
                          </span>
                        ) : (
                          "No CPT codes configured"
                        )}
                        {ruleset.allowOnDuplicateCptCodes &&
                          " • Allows duplicate codes"}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="border-top pt-4">
              {/* Case Rate Rule Sets */}
              {/* Show existing case rate rulesets if single provider/payer selected */}
              {existingCaseRateRulesets.length > 0 && (
                <div className="mt-3 p-3 border-1 border-300 border-round bg-blue-50">
                  <h6 className="mt-0 mb-2 text-blue-800">
                    <i className="pi pi-info-circle mr-2" />
                    Existing Case Rate Rule Sets for{" "}
                    {selectedProviders[0]?.name} - {selectedPayers[0]?.name}
                  </h6>
                  <p className="text-sm text-blue-700 mb-3">
                    These rulesets apply to this provider + payer combination
                    automatically:
                  </p>
                  {existingCaseRateRulesets.map((ruleset, index) => (
                    <div
                      key={ruleset.id}
                      className={
                        index < existingCaseRateRulesets.length - 1
                          ? "pb-2 mb-2 border-bottom"
                          : ""
                      }
                    >
                      <div className="font-medium">
                        <a
                          href={`/payer-rules/case-rate/edit/${ruleset.id}`}
                          onClick={(e) => {
                            e.preventDefault();
                            navigate(
                              `/payer-rules/case-rate/edit/${ruleset.id}`
                            );
                          }}
                          className="text-primary no-underline hover:underline cursor-pointer"
                        >
                          {ruleset.groupName || "Unnamed Case Rate Rule Set"}
                        </a>
                      </div>
                      <div className="text-sm text-600 mt-1">
                        {ruleset.cptCodes && ruleset.cptCodes.length > 0 ? (
                          <div>
                            Included CPT Codes:{" "}
                            {ruleset.cptCodes.slice(0, 10).join(", ")}
                            {ruleset.cptCodes.length > 10 && (
                              <span>
                                , and {ruleset.cptCodes.length - 10} other CPT
                                codes
                              </span>
                            )}
                          </div>
                        ) : (
                          <div>No included CPT codes</div>
                        )}
                        {ruleset.excludedCptCodes &&
                        ruleset.excludedCptCodes.length > 0 ? (
                          <div>
                            Excluded CPT Codes:{" "}
                            {ruleset.excludedCptCodes.slice(0, 10).join(", ")}
                            {ruleset.excludedCptCodes.length > 10 && (
                              <span>
                                , and {ruleset.excludedCptCodes.length - 10}{" "}
                                other CPT codes
                              </span>
                            )}
                          </div>
                        ) : (
                          <div>No excluded CPT codes</div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </Card>
        </div>

        {/* Inpatient Rulesets */}
        <div className="col-12">
          <Card title="Inpatient Rulesets" className="mb-4">
            <p className="mb-3 text-600">
              Inpatient rulesets apply automatically based on the provider and
              payer.
            </p>

            {existingInpatientRulesets.length > 0 && (
              <div className="mt-3 p-3 border-1 border-300 border-round bg-blue-50">
                <h6 className="mt-0 mb-2 text-blue-800">
                  <i className="pi pi-info-circle mr-2" />
                  Existing Inpatient Rulesets for {
                    selectedProviders[0]?.name
                  } - {selectedPayers[0]?.name}
                </h6>
                <p className="text-sm text-blue-700 mb-3">
                  These rulesets apply to this provider + payer combination
                  automatically:
                </p>
                {existingInpatientRulesets.map((ruleset, index) => (
                  <div
                    key={ruleset.id}
                    className={
                      index < existingInpatientRulesets.length - 1
                        ? "pb-2 mb-2 border-bottom"
                        : ""
                    }
                  >
                    <div className="font-medium">
                      <a
                        href={`/payer-rules/inpatient-rulesets/edit/${ruleset.id}`}
                        onClick={(e) => {
                          e.preventDefault();
                          navigate(
                            `/payer-rules/inpatient-rulesets/edit/${ruleset.id}`
                          );
                        }}
                        className="text-primary no-underline hover:underline cursor-pointer"
                      >
                        {ruleset.name || "Unnamed Inpatient Ruleset"}
                      </a>
                    </div>
                    <div className="text-sm text-600 mt-1">
                      Type: {ruleset.type?.replace(/_/g, " ") || "Unknown"}
                      {ruleset.drgCodes && ruleset.drgCodes.length > 0 && (
                        <span>
                          {" "}
                          | DRG Codes:{" "}
                          {ruleset.drgCodes.slice(0, 10).join(", ")}
                          {ruleset.drgCodes.length > 10 && (
                            <span>
                              , and {ruleset.drgCodes.length - 10} more
                            </span>
                          )}
                        </span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </Card>
        </div>

        {/* Exploding Fee Schedule Rulesets */}
        <div className="col-12">
          <Card title="Exploding Fee Schedule Rulesets" className="mb-4">
            <p className="mb-3 text-600">
              Exploding fee schedule rulesets apply automatically based on the
              provider and payer.
            </p>

            {existingExplodingFeeSchedules.length > 0 && (
              <div className="mt-3 p-3 border-1 border-300 border-round bg-blue-50">
                <h6 className="mt-0 mb-2 text-blue-800">
                  <i className="pi pi-info-circle mr-2" />
                  Existing Exploding Fee Schedule Rulesets for{" "}
                  {selectedProviders[0]?.name} - {selectedPayers[0]?.name}
                </h6>
                <p className="text-sm text-blue-700 mb-3">
                  These rulesets apply to this provider + payer combination
                  automatically:
                </p>
                {existingExplodingFeeSchedules.map((ruleset, index) => (
                  <div
                    key={ruleset.id}
                    className={
                      index < existingExplodingFeeSchedules.length - 1
                        ? "pb-2 mb-2 border-bottom"
                        : ""
                    }
                  >
                    <div className="font-medium">
                      <a
                        href={`/payer-rules/exploding-fee-schedules/edit/${ruleset.id}`}
                        onClick={(e) => {
                          e.preventDefault();
                          navigate(
                            `/payer-rules/exploding-fee-schedules/edit/${ruleset.id}`
                          );
                        }}
                        className="text-primary no-underline hover:underline cursor-pointer"
                      >
                        {ruleset.groupName ||
                          "Unnamed Exploding Fee Schedule Ruleset"}
                      </a>
                    </div>
                    <div className="text-sm text-600 mt-1">
                      {ruleset.cptCodes && ruleset.cptCodes.length > 0 ? (
                        <span>
                          CPT Codes: {ruleset.cptCodes.slice(0, 10).join(", ")}
                          {ruleset.cptCodes.length > 10 && (
                            <span>
                              , and {ruleset.cptCodes.length - 10} more
                            </span>
                          )}
                        </span>
                      ) : (
                        "No CPT codes configured"
                      )}
                      {ruleset.totalRateInCents != null && (
                        <span>
                          {" "}
                          | Total Rate: $
                          {(ruleset.totalRateInCents / 100).toLocaleString(
                            "en-US",
                            { minimumFractionDigits: 2 }
                          )}
                        </span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </Card>
        </div>

        {/* Package Rulesets */}
        <div className="col-12">
          <Card title="Package Rulesets" className="mb-4">
            <p className="mb-3 text-600">
              Package rulesets apply automatically based on the provider and
              payer.
            </p>

            {existingPackageRulesets.length > 0 && (
              <div className="mt-3 p-3 border-1 border-300 border-round bg-blue-50">
                <h6 className="mt-0 mb-2 text-blue-800">
                  <i className="pi pi-info-circle mr-2" />
                  Existing Package Rulesets for {
                    selectedProviders[0]?.name
                  } - {selectedPayers[0]?.name}
                </h6>
                <p className="text-sm text-blue-700 mb-3">
                  These rulesets apply to this provider + payer combination
                  automatically:
                </p>
                {existingPackageRulesets.map((ruleset, index) => (
                  <div
                    key={ruleset.id}
                    className={
                      index < existingPackageRulesets.length - 1
                        ? "pb-2 mb-2 border-bottom"
                        : ""
                    }
                  >
                    <div className="font-medium">
                      <a
                        href={`/payer-rules/packages/edit/${ruleset.id}`}
                        onClick={(e) => {
                          e.preventDefault();
                          navigate(`/payer-rules/packages/edit/${ruleset.id}`);
                        }}
                        className="text-primary no-underline hover:underline cursor-pointer"
                      >
                        {ruleset.groupName || "Unnamed Package Ruleset"}
                      </a>
                    </div>
                    <div className="text-sm text-600 mt-1">
                      {ruleset.triggerCptCodes &&
                      ruleset.triggerCptCodes.length > 0 ? (
                        <span>
                          Trigger:{" "}
                          {ruleset.triggerCptCodes.slice(0, 10).join(", ")}
                          {ruleset.triggerCptCodes.length > 10 && (
                            <span>
                              , and {ruleset.triggerCptCodes.length - 10} more
                            </span>
                          )}
                        </span>
                      ) : (
                        "No trigger codes"
                      )}
                      {ruleset.bundledCptCodes &&
                        ruleset.bundledCptCodes.length > 0 && (
                          <span>
                            {" "}
                            | Bundled:{" "}
                            {ruleset.bundledCptCodes.slice(0, 10).join(", ")}
                            {ruleset.bundledCptCodes.length > 10 && (
                              <span>
                                , and {ruleset.bundledCptCodes.length - 10} more
                              </span>
                            )}
                          </span>
                        )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </Card>
        </div>

        {/* Contract Attachments */}
        <div className="col-12">
          <Card title="Contract Attachments" className="mb-4">
            <p className="mb-3 text-600">
              Attach files to this contract for reference and download (PDFs,
              Excel, images, etc. up to 50MB each).
            </p>

            {formData.attachmentFiles.length === 0 && (
              <FileUpload
                name="attachments"
                multiple
                accept=".pdf,.doc,.docx,.xls,.xlsx,.csv,.txt,.rtf,.png,.jpg,.jpeg,.gif,.tif,.tiff,.bmp,.webp,.ppt,.pptx,.odt,.ods,.odp,.zip"
                maxFileSize={52428800}
                emptyTemplate={
                  <div className="text-center p-4">
                    <i className="pi pi-cloud-upload text-4xl text-400" />
                    <p className="mt-2 mb-0">
                      Drag and drop files here or click to browse.
                    </p>
                  </div>
                }
                mode="basic"
                chooseOptions={{
                  label: "Select Files",
                  icon: "pi pi-fw pi-plus",
                }}
                onSelect={(e) => {
                  const files = Array.from(e.files);
                  handleInputChange("attachmentFiles", [
                    ...formData.attachmentFiles,
                    ...files,
                  ]);
                }}
              />
            )}

            {/* Display selected attachment files */}
            {formData.attachmentFiles.length > 0 && (
              <div className="mt-4">
                <div className="flex justify-content-between align-items-center mb-3">
                  <h6 className="mb-0">
                    Selected Attachment Files ({formData.attachmentFiles.length}
                    )
                  </h6>
                  <Button
                    label="Add More Files"
                    icon="pi pi-plus"
                    className="p-button-outlined p-button-sm"
                    onClick={() => {
                      const input = document.createElement("input");
                      input.type = "file";
                      input.multiple = true;
                      input.accept =
                        ".pdf,.doc,.docx,.xls,.xlsx,.csv,.txt,.rtf,.png,.jpg,.jpeg,.gif,.tif,.tiff,.bmp,.webp,.ppt,.pptx,.odt,.ods,.odp,.zip";
                      input.onchange = (e) => {
                        const files = Array.from(
                          (e.target as HTMLInputElement).files || []
                        );
                        if (files.length > 0) {
                          handleInputChange("attachmentFiles", [
                            ...formData.attachmentFiles,
                            ...files,
                          ]);
                        }
                      };
                      input.click();
                    }}
                  />
                </div>
                <DataTable
                  value={formData.attachmentFiles}
                  className="p-datatable-sm"
                  emptyMessage="No attachment files selected."
                >
                  <Column
                    header="File Name"
                    body={(rowData: File) => rowData.name}
                  />
                  <Column
                    field="size"
                    header="Size"
                    body={(rowData) => {
                      const kb = rowData.size / 1024;
                      return kb >= 1024
                        ? `${(kb / 1024).toFixed(2)} MB`
                        : `${kb.toFixed(2)} KB`;
                    }}
                  />
                  <Column
                    header="Actions"
                    body={attachmentFileActionsTemplate}
                  />
                </DataTable>
              </div>
            )}
          </Card>
        </div>

        {/* Submit Actions */}
        <div className="col-12">
          {formData.rateCalculations.length === 0 && (
            <div className="mb-3 p-3 border-1 border-300 border-round bg-yellow-50">
              <div className="flex align-items-center">
                <i className="pi pi-exclamation-triangle text-yellow-600 mr-2" />
                <span className="text-yellow-800">
                  At least one rate calculation is required to save the contract
                  fee schedule.
                </span>
              </div>
            </div>
          )}
          {formData.providerIds.length > 0 && formData.payerIds.length > 0 && (
            <div className="mb-3 p-3 border-1 border-300 border-round bg-blue-50">
              <div className="flex align-items-center">
                <i className="pi pi-info-circle text-blue-600 mr-2" />
                <span className="text-blue-800">
                  This will create{" "}
                  <strong>
                    {formData.providerIds.length * formData.payerIds.length}
                  </strong>{" "}
                  contract
                  {formData.providerIds.length * formData.payerIds.length > 1
                    ? "s"
                    : ""}{" "}
                  ({formData.providerIds.length} provider
                  {formData.providerIds.length > 1 ? "s" : ""} ×{" "}
                  {formData.payerIds.length} payer
                  {formData.payerIds.length > 1 ? "s" : ""})
                </span>
              </div>
            </div>
          )}
          <div className="flex justify-content-end gap-2">
            <Button
              label={
                formData.providerIds.length * formData.payerIds.length > 1
                  ? `Create ${
                      formData.providerIds.length * formData.payerIds.length
                    } Contracts`
                  : "Save Contract"
              }
              icon="pi pi-check"
              onClick={handleSubmit}
              disabled={
                isSubmitting ||
                formData.rateCalculations.length === 0 ||
                hasBlockingDuplicateForSinglePair
              }
              loading={isSubmitting}
            />
          </div>
        </div>
      </div>
      <BaseRateFileUploadDialog
        visible={showUploadDialog}
        onHide={() => setShowUploadDialog(false)}
        accountId={accountId || undefined}
        onUploaded={(file) => {
          // Just select the freshly uploaded file in the dropdown; the user
          // then picks the type/rate and clicks Add (same as any other file).
          // The base rate files query is cache-invalidated on upload, so the
          // file appears as an option without a page refresh.
          setNewRateCalc((prev) => ({ ...prev, baseRateFileId: file.id }));
        }}
      />
    </div>
  );
}

export default CreateContractFeeSchedulePage;
